import React from 'react';
import {
  Box,
  Column,
  Flex,
  Loader,
  Row,
  Stack,
} from '@grubhubprod/gh-cookbook-react';
import { BalancePortalButton } from 'src/components/selfService/common/Balance/components/BalancePortalButton/BalancePortalButton';
import { PoweredByBalance } from 'src/components/selfService/common/Balance/components/PoweredByBalance/PoweredByBalance';
import { useBalanceContext } from 'src/components/selfService/common/Balance/hooks/useBalanceContext';
import { useFetchBalancePaymentMethods } from 'src/components/selfService/common/Balance/hooks/useFetchBalancePaymentMethods';
import { BalancePortalPage } from 'src/components/selfService/common/Balance/balance.types';
import { PageSectionTitle } from 'src/components/selfService/layout/PageSectionTitle/PageSectionTitle';
import { useScreenSizes } from 'src/utilities/useScreenSizes';
import { getSortedPaymentMethods } from 'src/components/selfService/common/Balance/utils/paymentMethodUtils/getSortedPaymentMethods';
import { PaymentMethodItemDisplay } from 'src/components/selfService/common/Balance/components/PaymentMethodItemDisplay/PaymentMethodItemDisplay';

const copy = {
  title: 'Payment methods',
  tooltip:
    "If you've applied for net terms, charges will appear on your due date. If not, charges will be applied to your payment method in real time.",
  descriptionNetTerms:
    "Your default payment method will be charged monthly on your due date. If you've added a bank account, it will be charged only if the default method fails, or for outstanding invoices. You can manage changes and updates to payment methods in the dedicated Balance portal.",
  descriptionNoNetTerms:
    "Your default payment method will be charged at checkout whenever meal credits are used. If you've added a bank account, it will be charged only if the default method fails, or for outstanding invoices. You can manage changes and updates to payment methods in the dedicated Balance portal.",
  descriptionGeneric:
    'Your default payment method will be used for all transactions. Any backup methods will only be charged if the default method fails, or for outstanding invoices. You can manage changes and updates to payment methods in the dedicated Balance portal.',
  manageButton: 'Manage payment methods',
} as const;

export function getDescription(isOnNetTerms: boolean | null): string {
  if (isOnNetTerms === true) return copy.descriptionNetTerms;
  if (isOnNetTerms === false) return copy.descriptionNoNetTerms;
  return copy.descriptionGeneric;
}

export function BalancePaymentMethodsSection(): JSX.Element {
  const { isDesktop } = useScreenSizes();
  const { externalBalanceBuyerId, isOnNetTerms } = useBalanceContext();
  const { isLoading, balancePaymentMethods } = useFetchBalancePaymentMethods(
    externalBalanceBuyerId
  );

  const sortedMethods = getSortedPaymentMethods(balancePaymentMethods);
  const description = getDescription(isOnNetTerms);

  if (isLoading) {
    return (
      <Box data-testid="balance-payment-methods-section">
        <Flex alignItems="center" justifyContent="center" minHeight="140px">
          <Loader size="md" />
        </Flex>
      </Box>
    );
  }

  return (
    <Box data-testid="balance-payment-methods-section">
      <Row>
        <Column tablet={12} desktop={9}>
          <PageSectionTitle
            title={copy.title}
            tooltipContent={copy.tooltip}
            subtitle={description}
            headingVariant="h4"
            y="md"
          />
          {sortedMethods.map((method) => (
            <Stack key={method.item.id} y="sm">
              <PaymentMethodItemDisplay method={method} />
            </Stack>
          ))}
        </Column>
        <Column tablet={12} desktop={3}>
          <Flex
            alignItems={isDesktop ? 'flex-end' : 'flex-start'}
            justifyContent={isDesktop ? 'space-between' : 'flex-start'}
            flexDirection="column"
            height="100%"
          >
            <Stack y="sm" alignSelf={isDesktop ? 'flex-end' : 'stretch'}>
              <BalancePortalButton
                balancePortalPage={BalancePortalPage.PAYMENT_METHODS}
                text={copy.manageButton}
                block={!isDesktop}
              />
            </Stack>
            <PoweredByBalance />
          </Flex>
        </Column>
      </Row>
    </Box>
  );
}
